import { useAtomValue } from "jotai";
import type { NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { useCanonicalUrl } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { AppFooter } from "scmp-app/components/app-footer";
import { GamesLoading } from "scmp-app/components/games/loading";
import { GameLoadingDuration } from "scmp-app/components/games/loading/consts";
import { useLoadingTimer } from "scmp-app/components/games/loading/hooks";
import { GameType } from "scmp-app/components/games/types";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import {
  useCrypticCrosswordSeo,
  useGameIframe,
  useQuickCrosswordSeo,
} from "scmp-app/pages/games/hooks";
import { GamePageSchema } from "scmp-app/pages/games/schema";
import { IframeContainer, IframeWrapper } from "scmp-app/pages/games/styles";

import { AdsContainer, PageContainer, StyledAdSlot } from "./styles";

const CrosswordGamePage: NextPage = () => {
  const router = useRouter();
  const { date: dateFromQuery, gameTitle: gameTitleFromQuery } = router.query;
  const date = Array.isArray(dateFromQuery) ? dateFromQuery[0] : (dateFromQuery ?? "");
  const gameTitle = Array.isArray(gameTitleFromQuery) ? gameTitleFromQuery[0] : gameTitleFromQuery;
  const type = (
    Array.isArray(router.query.gameType) ? router.query.gameType[0] : router.query.gameType
  ) as GameType;
  const validTypes = [GameType.Quick, GameType.Cryptic, GameType.WordSearch];
  const gameType = validTypes.includes(type) ? type : GameType.Quick;

  const { iframeId } = useGameIframe({
    date,
    path: `games/crosswords-${gameType}`,
  });

  const isLoading = useLoadingTimer(GameLoadingDuration);
  const adZone = `game/${gameType}crossword_gaming`;
  const canonicalPath = `/games/crosswords-${gameType}/${date}`;
  const { canonicalUrl } = useCanonicalUrl(canonicalPath);
  const quickSeo = useQuickCrosswordSeo(gameTitle ?? "PostMag Quick Crossword");
  const crypticSeo = useCrypticCrosswordSeo();
  const seo = gameType === ("cryptic" as GameType) ? crypticSeo : quickSeo;

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      autoRefreshOptions: {
        intervalRefreshOptions: {
          isDisable: false,
        },
        scrollBackRefreshOptions: {
          isDisable: true,
        },
      },
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      autoRefreshOptions: {
        intervalRefreshOptions: {
          isDisable: false,
        },
        scrollBackRefreshOptions: {
          isDisable: true,
        },
      },
      sizes: [
        [300, 100],
        [320, 100],
        [300, 50],
        [320, 50],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
  });

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  return (
    <PageContainer $theme={gameType}>
      <GamePageSchema />
      <PageSeo {...seo} canonical={canonicalUrl} />
      <IframeContainer $isLoading={isLoading}>
        <IframeWrapper id={iframeId} />
        <GamesLoading
          date={date}
          duration={GameLoadingDuration}
          gameTitle={gameTitle}
          isLoading={isLoading}
          theme={gameType}
        />
      </IframeContainer>

      {!isLoading && (
        <AdsContainer $theme={gameType}>
          <StyledAdSlot
            adUnit="d_banner2"
            breakpoint={theme.breakpoints.up("tablet")}
            sizes={[
              [728, 90],
              [970, 90],
            ]}
            targeting={{
              paid: TargetingPaidType.NotArticle,
            }}
            withLabel={true}
            zone={adZone}
          />
        </AdsContainer>
      )}
      {!isLoading && <AppFooter variant="games" />}
    </PageContainer>
  );
};

const pageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 12,
      tabletUp: 0,
    },
  },
  pageConfiguration: {
    responsivePadding: {
      tabletUp: 24,
    },
  },
};

CrosswordGamePage.getInitialProps = () => pageProps;

export default CrosswordGamePage;
